export type InvestorLinkPreview = {
  startupName: string;
  tagline: string | null;
  description: string;
  label: string;
  available: boolean;
  reason?: "revoked" | "expired";
  startupId: string;
};

type StartupPreviewFields = {
  name?: string;
  tagline?: string;
  description?: string;
};

type BusinessPlanContextPreviewFields = {
  investorPitch?: string;
  valueProposition?: string;
};

const MAX_DESCRIPTION_LENGTH = 200;
const DEFAULT_SITE_ORIGIN = "https://burn-chart.com";
const DEFAULT_OG_IMAGE = `${DEFAULT_SITE_ORIGIN}/apple-touch-icon.svg`;

export function truncateForMeta(value: string, maxLength = MAX_DESCRIPTION_LENGTH): string {
  const trimmed = value.trim().replace(/\s+/g, " ");
  if (trimmed.length <= maxLength) {
    return trimmed;
  }
  return `${trimmed.slice(0, maxLength - 1).trimEnd()}…`;
}

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function pickInvestorShareDescription(
  startup: StartupPreviewFields,
  context?: BusinessPlanContextPreviewFields | null,
): string {
  const startupName = startup.name?.trim() || "this startup";
  const candidates = [
    startup.tagline,
    startup.description,
    context?.investorPitch,
    context?.valueProposition,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      return truncateForMeta(candidate);
    }
  }

  return `View the investor portal for ${startupName}, including financial projections and business plan.`;
}

export function buildInvestorShareTitle(startupName: string): string {
  const name = startupName.trim() || "Startup";
  return `${name} — Investor Portal`;
}

export function buildInvestorShareMetaTags(
  preview: InvestorLinkPreview,
  pageUrl: string,
  imageUrl = DEFAULT_OG_IMAGE,
): string {
  const title = buildInvestorShareTitle(preview.startupName);
  const description = preview.description;
  const safeTitle = escapeHtml(title);
  const safeDescription = escapeHtml(description);
  const safeUrl = escapeHtml(pageUrl);
  const safeImage = escapeHtml(imageUrl);

  return [
    `<title>${safeTitle}</title>`,
    `<meta name="description" content="${safeDescription}" />`,
    `<meta property="og:type" content="website" />`,
    `<meta property="og:site_name" content="BurnChart" />`,
    `<meta property="og:title" content="${safeTitle}" />`,
    `<meta property="og:description" content="${safeDescription}" />`,
    `<meta property="og:url" content="${safeUrl}" />`,
    `<meta property="og:image" content="${safeImage}" />`,
    `<meta name="twitter:card" content="summary" />`,
    `<meta name="twitter:title" content="${safeTitle}" />`,
    `<meta name="twitter:description" content="${safeDescription}" />`,
    `<meta name="twitter:image" content="${safeImage}" />`,
  ].join("\n    ");
}

export function injectInvestorShareMeta(html: string, metaBlock: string): string {
  const withoutTitle = html.replace(/<title>[\s\S]*?<\/title>\s*/i, "");
  const withoutDescription = withoutTitle.replace(
    /<meta\s+name="description"[\s\S]*?\/?>\s*/gi,
    "",
  );

  return withoutDescription.replace("</head>", `    ${metaBlock}\n  </head>`);
}
